import React, { MouseEvent, useCallback } from 'react';
import { BsLink45Deg } from '@react-icons/all-files/bs/BsLink45Deg';
import { useContextSelector } from 'use-context-selector';
import { BucketLogData } from '@bucketplace/bucket-log';
import { useSnackbar } from '@bucketplace/design-system';

import { EditorPlaceholder } from '../../../components';
import { EditorContext, useEditorInfoContext, useImageMapShareContext } from '../../../contexts';

/** 공유하기 platform type */
type Platform = 'kakaotalk' | 'url';

export interface ImageMapShareButtonProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  platform: Platform;
  logData?: BucketLogData;
}

export function ImageMapShareButton({
  children,
  ...props
}: ImageMapShareButtonProps) {
  return (
    <ShareButton {...props}>
      { children || (
        <EditorPlaceholder
          icon={<BsLink45Deg />}
          message="아이템을 추가해주세요."
        />
      ) }
    </ShareButton>
  );
}

function ShareButton({
  children,
  className,
  style,
  platform,
  logData,
}: ImageMapShareButtonProps): React.ReactElement {
  const { open } = useSnackbar();
  const {
    kakaoApi,
    kakaoTemplateId,
    templateArgs,
    onClick,
  } = useImageMapShareContext();
  const { onSendLog } = useEditorInfoContext();
  const { displayMode } = useContextSelector(
    EditorContext,
    (context) => context!.state,
  );

  const handleClick = useCallback(async (e: MouseEvent): Promise<void> => {
    e.preventDefault();

    if (displayMode === 'edit') {
      return;
    }

    if (logData) {
      onSendLog?.(logData);
    }

    if (platform === 'kakaotalk' && kakaoApi != null) {
      try {
        kakaoApi.Share.sendCustom({
          templateId: kakaoTemplateId,
          templateArgs,
          installTalk: true,
        });
      } catch (err) {
        console.error(err);
        open({ content: '카카오톡 공유에 실패했습니다.' });
      }
    } else {
      onClick?.();
    }
  }, [platform, displayMode, kakaoApi, open]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      style={style}
    >
      { children }
    </button>
  );
}
